/**
 * useModelFieldLabels Hook
 *
 * Purpose: Expose the field labels a metadata-driven form renders for a model's view so
 *          non-form surfaces (Maestro's field-mapping wizard, approval cards) can display the
 *          exact same text instead of maintaining a parallel hardcoded label table (ORCH-381),
 *          and the view's Field rows themselves so those surfaces can reuse the form's own
 *          FieldRenderers rather than a parallel input (ORCH-374).
 * Architecture: Reads the same `['metadata-view', modelId, viewType, tenantId]` React Query
 *               entry that useTemplateForm / useMetadataForm populate, so a page already
 *               rendering the form resolves labels straight from cache with no extra request.
 *               Label resolution mirrors FieldRenderers exactly: `label_override` wins over the
 *               FieldDefinition label.
 * Dependencies: @tanstack/react-query (useQuery), metadataService (getModelView),
 *               components/metadata-form/types (ViewField, ViewConfiguration)
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { metadataService } from '../services/metadataService';
import type { ViewField, ViewConfiguration } from '../components/metadata-form/types';

export type MetadataViewType = 'edit-modal' | 'table' | 'list' | 'detail';

/**
 * metadataViewQueryKey — shared React Query key for a model's view configuration
 *
 * Purpose: Keeps every consumer of `/models/{model_id}/view` on one cache entry. Forms
 *          (useTemplateForm, useMetadataForm) and label consumers must agree on this key,
 *          otherwise a label lookup silently fires its own request instead of reusing the
 *          view the form already fetched.
 */
export const metadataViewQueryKey = (
  modelId: string,
  viewType: string,
  tenantId: string
): readonly unknown[] => ['metadata-view', modelId, viewType, tenantId];

/**
 * effectiveFieldLabel — the label a form actually renders for one view field
 *
 * Purpose: Single expression for "what does this field look like to a user", matching
 *          metadata-form/FieldRenderers (`label_override || field_definition.label`).
 * Input: field — a ViewField from the model view configuration
 * Output: the display label, falling back to the fielddef_id if metadata has neither
 */
export const effectiveFieldLabel = (field: ViewField): string =>
  field.label_override || field.field_definition?.label || field.fielddef_id;

/**
 * buildFieldLabelMap — fielddef_id → display label for a view's fields
 *
 * Input: fields — ViewField rows from a ViewConfiguration (may be undefined while loading)
 * Output: lookup map; empty when the view has not loaded
 */
export const buildFieldLabelMap = (
  fields?: readonly ViewField[] | null
): Record<string, string> => {
  const map: Record<string, string> = {};
  (fields ?? []).forEach((field) => {
    if (!field?.fielddef_id) return;
    map[field.fielddef_id] = effectiveFieldLabel(field);
  });
  return map;
};

/**
 * humanizeFdKey — last-resort label for a field the metadata view doesn't describe
 *
 * Purpose: Strips the `fd_` prefix and title-cases the remainder, so a field that is hidden
 *          in the view (or belongs to another model) still renders readable text instead of a
 *          technical key.
 * Input: fdKey — e.g. `fd_start-date`
 * Output: e.g. `Start Date`
 */
export const humanizeFdKey = (fdKey: string): string => {
  if (!fdKey) return '';
  return fdKey
    .replace(/^fd[_-]/i, '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * resolveFieldLabel — pure label resolution against a metadata label map
 *
 * Purpose: Shared fallback chain so every surface degrades the same way when metadata is
 *          still loading or doesn't describe the field.
 * Logic: metadata label → caller-supplied fallback (e.g. an agent payload's field_label,
 *        ignored when it still looks like a technical key) → humanized fd key → the raw key.
 */
export const resolveFieldLabel = (
  fdKey: string,
  labels: Record<string, string>,
  fallbackLabel?: string | null
): string => {
  const fromMetadata = labels[fdKey];
  if (fromMetadata) return fromMetadata;
  const supplied = (fallbackLabel ?? '').trim();
  if (supplied && !/^fd[_-]/i.test(supplied)) return supplied;
  return humanizeFdKey(fdKey) || fdKey;
};

/**
 * buildViewFieldMap — fielddef_id → ViewField for a view's fields
 *
 * Purpose: Lets a non-form surface hand the *same* metadata field the form renders to a
 *          shared FieldRenderer (ORCH-374: Maestro's Budget question renders the plan
 *          form's own Budget field, so precision and formatting can never diverge).
 * Input: fields — ViewField rows from a ViewConfiguration (may be undefined while loading)
 * Output: lookup map; empty when the view has not loaded
 */
export const buildViewFieldMap = (
  fields?: readonly ViewField[] | null
): Record<string, ViewField> => {
  const map: Record<string, ViewField> = {};
  (fields ?? []).forEach((field) => {
    if (!field?.fielddef_id) return;
    map[field.fielddef_id] = field;
  });
  return map;
};

/**
 * orderByViewFields — sort field keys into the view's display_order
 *
 * Purpose: The metadata view is the one source of field order (ORCH-405). A non-form
 *          surface that walks a subset of the form's fields (Maestro's chip row) sorts its
 *          keys through this so it always matches the form, including tenant view overrides.
 * Logic: Stable sort by the view field's display_order; keys the view doesn't describe
 *        (or before it loads) keep their given relative order, after the described ones.
 * Input: keys — fielddef_ids in fallback order; fields — ViewField rows (may be undefined)
 * Output: a new array with the same keys
 */
export const orderByViewFields = (
  keys: readonly string[],
  fields?: readonly ViewField[] | null
): string[] => {
  const orderOf: Record<string, number> = {};
  (fields ?? []).forEach((field) => {
    if (field?.fielddef_id && typeof field.display_order === 'number') {
      orderOf[field.fielddef_id] = field.display_order;
    }
  });
  return keys
    .map((key, index) => ({ key, index, order: orderOf[key] ?? Number.POSITIVE_INFINITY }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ key }) => key);
};

export interface ModelFieldLabels {
  /** fielddef_id → label, exactly as the form renders it. Empty until the view loads. */
  labels: Record<string, string>;
  isLoading: boolean;
  /** Resolve one field's label with the shared fallback chain. */
  labelFor: (fdKey: string, fallbackLabel?: string | null) => string;
  /** The view's own Field row for a key, so a shared renderer can be reused outside the form. */
  fieldFor: (fdKey: string) => ViewField | undefined;
  /** Sort field keys into the view's display_order (ORCH-405). */
  orderKeys: (keys: readonly string[]) => string[];
}

/**
 * useModelFieldLabels — metadata-driven field labels for a model's view
 *
 * Input: modelId / viewType / tenantId — the same triple a form passes to getModelView
 * Output: ModelFieldLabels — the label map plus a resolver with fallbacks
 */
export const useModelFieldLabels = (
  modelId: string,
  viewType: MetadataViewType,
  tenantId: string
): ModelFieldLabels => {
  const { data: viewConfig, isLoading } = useQuery<ViewConfiguration>({
    queryKey: metadataViewQueryKey(modelId, viewType, tenantId),
    queryFn: async () => {
      const response = await metadataService.getModelView(modelId, tenantId, viewType);
      return response as unknown as ViewConfiguration;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!modelId && !!tenantId,
  });

  const labels = useMemo(() => buildFieldLabelMap(viewConfig?.fields), [viewConfig]);
  const fields = useMemo(() => buildViewFieldMap(viewConfig?.fields), [viewConfig]);

  const labelFor = useCallback(
    (fdKey: string, fallbackLabel?: string | null) =>
      resolveFieldLabel(fdKey, labels, fallbackLabel),
    [labels]
  );

  const fieldFor = useCallback((fdKey: string) => fields[fdKey], [fields]);

  const orderKeys = useCallback(
    (keys: readonly string[]) => orderByViewFields(keys, viewConfig?.fields),
    [viewConfig]
  );

  return { labels, isLoading, labelFor, fieldFor, orderKeys };
};

/** Model + view that back the Create Media Plan form — Maestro mirrors these labels. */
export const CREATE_PLAN_MODEL_ID = 'mediaplan';
export const CREATE_PLAN_VIEW_TYPE: MetadataViewType = 'edit-modal';

/**
 * useCreatePlanFieldLabels — Create Media Plan labels for Maestro surfaces (ORCH-381)
 *
 * Purpose: Maestro's chip row, active-field header and approval cards must read the same
 *          labels as the Create Media Plan form on the left of the page. Calling this hook
 *          (rather than hardcoding a table per component) is what keeps them from diverging.
 */
export const useCreatePlanFieldLabels = (tenantId: string): ModelFieldLabels =>
  useModelFieldLabels(CREATE_PLAN_MODEL_ID, CREATE_PLAN_VIEW_TYPE, tenantId);

export default useModelFieldLabels;
